import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { useQueryClient } from '@tanstack/react-query';
import React, { memo, useMemo } from 'react';

import {
  AssigneeDropdown,
  type AssigneeOption,
  type AssigneeOptionGroup,
} from '@/components/AssigneeDropdown';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import {
  filterAssigneeGroups,
  useAssigneeOptions,
  type UseAssigneeOptionsReturn,
} from '@/hooks/useAssigneeOptions';
import { useBelowProgramAdminUsers } from '@/hooks/useBelowProgramAdminUsers';
import { useUpdateAssignment } from '@/hooks/useCases';
import { useUsersByIds } from '@/hooks/useUsers';
import { formatAssigneeTooltip, resolveAssigneeTeamName } from '@/utils/assigneeTeam';
import { isHumanAuditForProgramAdmins } from '@/utils/caseActions';
import { isHumanCallInProgress } from '@/utils/caseStateHelpers';

/** Sentinel group id for the "currently assigned but not in any team" fallback option. */
const NO_TEAM_GROUP_ID = '__no_team__';

/** Map a group id from the dropdown to a real team id, or undefined when there is none. */
const normalizeTeamId = (teamId?: string): string | undefined =>
  teamId && teamId !== NO_TEAM_GROUP_ID ? teamId : undefined;

interface AssigneeSelectProps {
  /** Case record item */
  item: GetCaseRecordResponse;
  /**
   * Callback when an assignee is selected. Sends the assignee email (not user ID) and, when
   * the assignee was picked from a team group, that team's id.
   */
  onAssigneeSelect?: (assigneeEmail: string, caseId: string, teamId?: string) => void;
  /** Additional styling for the trigger */
  triggerClassName?: string;
  /**
   * Injected assignee options. When provided, these are used instead of the
   * program-scoped `useAssigneeOptions` hook — required by cross-program /
   * org-wide views (e.g. the Tier-2 org PQC list) where there is no single
   * selected program in the store to source teams from.
   */
  optionsOverride?: UseAssigneeOptionsReturn;
}

const AssigneeSelectComponent: React.FC<AssigneeSelectProps> = ({
  item,
  onAssigneeSelect,
  triggerClassName,
  optionsOverride,
}) => {
  const queryClient = useQueryClient();
  const updateAssignment = useUpdateAssignment();
  // Program-scoped options; unused when a cross-program override is injected, but
  // the hook must still run unconditionally (rules of hooks).
  const programOptions = useAssigneeOptions(item.workflow_type);
  const {
    allUserIds,
    usersMap: teamUsersMap,
    groups,
    isLoading,
  } = optionsOverride ?? programOptions;

  // Always resolve the assigned user so we can show their avatar even on completed cases
  const assignedUserId = item.assigned_user_id || '';
  const { usersMap: assignedUserMap } = useUsersByIds(assignedUserId ? [assignedUserId] : []);

  // Merged map: team members + assigned user (so assigned user is always resolvable)
  const usersMap = useMemo(() => {
    const merged = new Map(teamUsersMap);
    for (const [id, user] of assignedUserMap) {
      if (!merged.has(id)) merged.set(id, user);
    }
    return merged;
  }, [teamUsersMap, assignedUserMap]);

  // Build a set of all member emails for quick lookup
  const memberEmails = useMemo(() => {
    const emails = new Set<string>();
    for (const memberId of allUserIds) {
      const user = usersMap.get(memberId);
      emails.add(user?.email || memberId);
    }
    return emails;
  }, [allUserIds, usersMap]);

  // A transportation or unifiedmedbv case in human_audit can be assigned only to a Program
  // Admin or a higher role (BV-2657): users whose role on the case's program is below
  // that are not offered. Dashboard only; the server does not check it.
  const reservedForAdmins = isHumanAuditForProgramAdmins(item.state, item.workflow_type);
  const casePrograms = useMemo(() => (item.program_id ? [item.program_id] : []), [item.program_id]);
  const { isBelow, isLoading: rolesLoading } = useBelowProgramAdminUsers({
    organizationId: item.organization_id,
    programIds: casePrograms,
    userIds: allUserIds,
    enabled: reservedForAdmins && !!item.status_flags?.blocked_on_manual_task,
  });
  const assignable = useMemo(
    () =>
      reservedForAdmins
        ? filterAssigneeGroups(groups, userId => !isBelow(item.program_id, userId))
        : { groups, allUserIds },
    [reservedForAdmins, groups, allUserIds, isBelow, item.program_id],
  );

  const hasUsers = assignable.allUserIds.length > 0;
  // A case whose human call is live stays with the rep on the phone: read-only (LA-8061).
  // The server rejects the reassign too.
  const canEdit =
    item.status_flags?.blocked_on_manual_task &&
    hasUsers &&
    !isHumanCallInProgress(item.state, item.active_call_id);

  // Resolve the assigned user by ID first, then fall back to email-based reverse lookup.
  const assignedUser = useMemo(() => {
    if (assignedUserId) {
      const byId = usersMap.get(assignedUserId);
      if (byId) return byId;
    }
    if (item.assigned) {
      for (const user of usersMap.values()) {
        if (user.email === item.assigned) return user;
      }
    }
    return undefined;
  }, [assignedUserId, item.assigned, usersMap]);

  const assignedEmail = assignedUser?.email || (item.assigned?.includes('@') ? item.assigned : '');
  const assignedDisplay = assignedUser?.name || assignedEmail || item.assigned;
  const showExtraAssigned = assignedEmail && !memberEmails.has(assignedEmail);

  // BV-2228: the tooltip used to repeat the name already rendered beside it.
  // The team is what it is for — resolved from the same groups the dropdown is
  // built from, so it costs no extra request.
  const teamOptions = useMemo(
    () => groups.map(group => ({ id: group.teamId, name: group.teamName })),
    [groups],
  );
  const assigneeTooltip = formatAssigneeTooltip(
    assignedDisplay,
    resolveAssigneeTeamName(item.team_id, teamOptions),
  );

  // Build team-grouped options for the dropdown. The currently-assigned user who isn't in any
  // workflow team goes into a leading no-heading group, so selecting them sends no team_id.
  const options = useMemo<AssigneeOptionGroup[]>(() => {
    const result: AssigneeOptionGroup[] = [];
    if (showExtraAssigned) {
      result.push({
        id: NO_TEAM_GROUP_ID,
        label: '',
        options: [{ id: assignedEmail, email: assignedEmail }],
      });
    }
    for (const group of assignable.groups) {
      result.push({
        id: group.teamId,
        label: group.teamName,
        options: group.members.map<AssigneeOption>(member => ({
          id: member.userId,
          name: member.name || undefined,
          email: member.email,
          available: usersMap.get(member.userId)?.available ?? false,
        })),
      });
    }
    return result;
  }, [assignable.groups, usersMap, showExtraAssigned, assignedEmail]);

  // Read-only display when the case can't be edited
  if (!canEdit || updateAssignment.isPending || isLoading || rolesLoading) {
    const isUnassigned = !item.assigned;
    // min-w-0 lets the name/email span shrink below its content width so `truncate`
    // can ellipsis it inside the fixed-width Assignee column instead of overflowing.
    return (
      <div
        className={`flex min-w-0 max-w-full items-center gap-2 px-1 py-2 text-sm ${isUnassigned ? 'text-muted-foreground' : ''}`}
      >
        {item.assigned && assignedUser ? (
          <>
            <span className="shrink-0">
              <UserAvatar
                name={assignedUser.name}
                email={assignedUser.email}
                userId={assignedUser.id}
                size="sm"
                available={assignedUser.available ?? false}
              />
            </span>
            <SimpleTooltipV2 content={assigneeTooltip} className="min-w-0">
              <span className="truncate">{assignedDisplay}</span>
            </SimpleTooltipV2>
          </>
        ) : (
          <SimpleTooltipV2
            content={item.assigned ? assigneeTooltip : undefined}
            className="min-w-0"
          >
            <span className="truncate">{item.assigned ? assignedDisplay : 'Unassigned'}</span>
          </SimpleTooltipV2>
        )}
      </div>
    );
  }

  return (
    <AssigneeDropdown
      mode="single"
      variant="inline"
      selected={assignedEmail}
      selectedTeamId={item.team_id}
      options={options}
      onChange={(email, teamId) => onAssigneeSelect?.(email, item.id!, normalizeTeamId(teamId))}
      onOpen={() => queryClient.invalidateQueries({ queryKey: ['users', 'byIds'] })}
      placeholder="Unassigned"
      searchPlaceholder="Search assignee..."
      triggerTestId="assign-to-user"
      className={triggerClassName}
    />
  );
};

// Memoize the component to prevent unnecessary re-renders
export const AssigneeSelect = memo(AssigneeSelectComponent);
